/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Me · Attendance — punch history list + link to full calendar
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CalendarDays, Clock, MapPin, ArrowRight } from "lucide-react";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", {
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch { return "—"; }
}
function fmtDate(d) {
  if (!d) return "";
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
  } catch { return d; }
}

export default function MeAttendance() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/my-portal/punch/history?limit=30")
      .then((r) => { if (!cancelled) setHistory(r.data || []); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-3" data-testid="me-attendance">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">My Attendance</div>
        <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>Punches &amp; Calendar</h1>
        <p className="text-[11px] text-slate-500">Recent self-service punches from your phone</p>
      </div>

      <Link to="/my-portal/attendance"
        data-testid="me-att-cal-link"
        className="block bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl p-4 flex items-center justify-between hover:opacity-95">
        <div className="flex items-center gap-3">
          <CalendarDays className="h-6 w-6" />
          <div>
            <div className="text-sm font-bold">Full Month Calendar</div>
            <div className="text-[11px] opacity-90">P / A / H / Leaves with details</div>
          </div>
        </div>
        <ArrowRight className="h-5 w-5" />
      </Link>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 px-1">
          Recent Punches
        </div>
        {loading ? (
          <div className="text-center text-xs text-slate-400 py-8">Loading…</div>
        ) : history.length === 0 ? (
          <div className="text-center text-xs text-slate-400 py-8 bg-white rounded-xl ring-1 ring-slate-200">
            No punches yet — use Home tab to punch in.
          </div>
        ) : (
          <div className="space-y-1.5">
            {history.map((p) => (
              <div key={p.id} className="bg-white rounded-xl ring-1 ring-slate-200 p-3"
                   data-testid={`me-att-row-${p.date}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-bold text-slate-900">{fmtDate(p.date)}</div>
                  <div className="text-[10px] text-slate-500">{p.date}</div>
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-[11px]">
                  <div className="flex items-center gap-1 text-emerald-700">
                    <Clock className="h-3 w-3" /> In <strong className="ml-1">{fmt(p.punch_in_at)}</strong>
                  </div>
                  <div className="flex items-center gap-1 text-red-700">
                    <Clock className="h-3 w-3" /> Out <strong className="ml-1">{fmt(p.punch_out_at)}</strong>
                  </div>
                  {p.worked_minutes != null && p.worked_minutes > 0 && (
                    <div className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold">
                      {Math.floor(p.worked_minutes / 60)}h {p.worked_minutes % 60}m
                    </div>
                  )}
                </div>
                {(p.punch_in_loc_label || p.punch_out_loc_label) && (
                  <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1 truncate">
                    <MapPin className="h-3 w-3 flex-shrink-0" />
                    {p.punch_in_loc_label || p.punch_out_loc_label}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
